import { Controller } from "@hotwired/stimulus"
import { parseWidth, isValidWidth, widthRange, displayWidth } from "@/lib/Listing/SpaceFit.js"

const ACTIVE_UNIT = ["bg-blackest", "text-white"]
const APPLY_IDLE = "bg-grey-dark"
const APPLY_PENDING = "bg-black"

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1)

export default class extends Controller {
  static targets = [
    "field", "room", "anchor", "idea", "widthInput", "unitButton", "unitSuffix", "error", "submit", "applyButton",
    "stepSummary", "question", "widthLabel", "ideasHeading", "ideaRange",
  ]
  static values = { config: Object }

  #unit = null

  connect() {
    this.hydrate()
  }

  hydrate() {
    // Room is left unpicked so analytics only see rooms buyers chose; anchor and idea need a value to preview.
    for (const group of [this.anchorTargets, this.ideaTargets]) this.#checkFirstIfNone(group)
    const cm = parseInt(this.#field("width")?.value, 10)
    if (!this.#field("anchor")?.value || !cm) return this.refresh()

    this.#select(this.roomTargets, this.#field("room").value)
    this.#select(this.anchorTargets, this.#field("anchor").value)
    this.#select(this.ideaTargets, this.#field("idea").value)
    this.#unit = cm >= 100 ? "m" : "cm"
    if (this.hasWidthInputTarget) this.widthInputTarget.value = this.#unit === "m" ? cm / 100 : cm
    this.fieldTargets.forEach((field) => { field.disabled = false })
    this.refresh()
  }

  // Every valid width reads one way only (under 10 is metres, 40+ is centimetres),
  // so typing re-derives the unit and a toggled unit only lasts until the next keystroke.
  widthChanged() {
    this.#unit = null
    this.edit()
  }

  edit() {
    this.changed()
    this.refresh()
  }

  // Desktop applies on the Apply button only, so it stays muted until the Size section has a pending change.
  changed() {
    this.applyButtonTargets.forEach((button) => button.classList.replace(APPLY_IDLE, APPLY_PENDING))
  }

  // Apply and Enter: a typed width applies the fit; an invalid one stays put with its error showing;
  // with no width the form submits as before, for the max width/height fields.
  submit() {
    if (!this.#rawWidth) {
      this.clear()
      return this.#form?.requestSubmit()
    }

    const width = this.#width
    if (width && isValidWidth(this.configValue, width.cm)) this.apply()
  }

  setUnit({ currentTarget }) {
    this.#unit = currentTarget.dataset.unit
    this.refresh()
  }

  refresh() {
    const anchor = this.#choice(this.anchorTargets)
    if (!anchor) return

    const room = this.#choice(this.roomTargets)
    const width = this.#width
    const valid = Boolean(width) && isValidWidth(this.configValue, width.cm)
    const noun = anchor.dataset.label

    const roomLabel = room?.value ? room.dataset.label ?? room.text : null
    const place = anchor.dataset.place
    this.#fill(this.stepSummaryTargets, { where: roomLabel ? `${roomLabel}, ${place}` : capitalize(place) })
    this.#fill(this.questionTargets, { anchor: noun })
    this.#fill(this.widthLabelTargets, { anchor: capitalize(noun) })
    this.#showUnit(width?.unit ?? this.#unit ?? "m")
    this.errorTargets.forEach((error) => error.classList.toggle("hidden", valid || !this.#rawWidth))
    this.submitTargets.forEach((button) => { button.disabled = !valid })
    this.#showIdeas(valid ? width.cm : null, anchor.value, noun)
    this.#tagForCapture({ room: room?.value, anchor: anchor.value, idea: this.#idea, "width-cm": width?.cm, unit: width?.unit })
    this.applyButtonTargets.forEach((button) => {
      if (valid) button.setAttribute("data-ph-capture-attribute-fits-step", "submit")
      else button.removeAttribute("data-ph-capture-attribute-fits-step")
    })
  }

  apply() {
    const width = this.#width
    if (!width || !isValidWidth(this.configValue, width.cm)) return

    const values = {
      room: this.#choice(this.roomTargets)?.value,
      anchor: this.#choice(this.anchorTargets)?.value,
      width: width.cm,
      idea: this.#idea,
    }
    this.fieldTargets.forEach((field) => {
      field.value = values[field.dataset.field] ?? ""
      field.disabled = false
    })
    this.#clearSizeFilters()
    this.#form?.requestSubmit()
  }

  clear() {
    this.fieldTargets.forEach((field) => {
      field.value = ""
      field.disabled = true
    })
    if (this.hasWidthInputTarget) this.widthInputTarget.value = ""
    this.refresh()
  }

  get #rawWidth() {
    return this.hasWidthInputTarget ? this.widthInputTarget.value.trim() : ""
  }

  get #width() {
    return this.#rawWidth ? parseWidth(this.#rawWidth, this.#unit) : null
  }

  get #idea() {
    return this.#choice(this.ideaTargets)?.value || this.#field("idea")?.value || this.configValue.ideas[0]
  }

  get #form() {
    return this.element.closest("form") ?? this.element.querySelector("form")
  }

  #field(name) {
    return this.fieldTargets.find((field) => field.dataset.field === name)
  }

  #choice(targets) {
    const select = targets.find((target) => target.tagName === "SELECT")
    return select ? select.selectedOptions[0] : targets.find((target) => target.checked)
  }

  #select(targets, value) {
    if (!value) return
    targets.forEach((target) => {
      if (target.tagName === "SELECT") target.value = value
      else target.checked = target.value === value
    })
  }

  // Radios start unchecked so the cached filter markup carries no selection.
  #checkFirstIfNone(radios) {
    if (radios.length && radios[0].type === "radio" && !radios.some((radio) => radio.checked)) radios[0].checked = true
  }

  #fill(targets, vars) {
    targets.forEach((target) => {
      target.textContent = target.dataset.template.replace(/\{(\w+)\}/g, (_, key) => vars[key] ?? "")
    })
  }

  #showUnit(unit) {
    this.unitButtonTargets.forEach((button) => {
      ACTIVE_UNIT.forEach((name) => button.classList.toggle(name, button.dataset.unit === unit))
    })
    this.unitSuffixTargets.forEach((suffix) => { suffix.textContent = unit })
  }

  #showIdeas(widthCm, anchor, noun) {
    this.ideasHeadingTargets.forEach((heading) => heading.classList.toggle("hidden", !widthCm))
    if (!widthCm) {
      this.ideaRangeTargets.forEach((range) => { range.textContent = "" })
      return
    }

    this.#fill(this.ideasHeadingTargets, { width: displayWidth(widthCm), anchor: noun })
    this.ideaRangeTargets.forEach((range) => {
      const [min, max] = widthRange(this.configValue, anchor, widthCm, range.dataset.idea)
      this.#fill([range], { min, max })
    })
  }

  #tagForCapture(props) {
    const elements = [...this.submitTargets, ...this.applyButtonTargets]
    Object.entries(props).forEach(([key, value]) => {
      elements.forEach((element) => element.setAttribute(`data-ph-capture-attribute-fits-${key}`, value ?? ""))
    })
  }

  #clearSizeFilters() {
    this.element.querySelectorAll("input[name='size[]']").forEach((checkbox) => { checkbox.checked = false })
    this.element.querySelectorAll("input[name='width_max'], input[name='height_max']").forEach((input) => { input.value = "" })
  }
}
